import network from '@/services/network';
import {
  ApiResponse,
  CreateFeedbackRequest,
  Feedback,
  FeedbackAssignee,
  FeedbackStatus,
  TicketArt,
  TicketArtOption,
  UpdateFeedbackRequest,
} from '@/types';
import { ErrorResponse } from '@/utils';

/** Liste + Auswahlliste der Bearbeiter in einer Antwort (F4). */
interface FeedbackListResponse extends ApiResponse<Feedback[]> {
  meta?: {
    assignees: FeedbackAssignee[];
    /**
     * Anzahl je Status ueber den GESAMTEN Bestand (B6, 28.08.2026) — nicht
     * ueber die gefilterte Liste. Der Reiter, den man gerade NICHT ansieht,
     * braucht seine Zahl trotzdem.
     */
    counts?: Record<FeedbackStatus, number>;
    /**
     * Die gepflegten Arten — ALLE, auch abgeschaltete. Die Triage uebersetzt
     * damit den Slug eines alten Tickets, selbst wenn die Art inzwischen
     * nicht mehr waehlbar ist.
     */
    ticket_arten?: TicketArtOption[];
  };
}

type FeedbackPatchResult = Pick<
  Feedback,
  'id' | 'assigned_to' | 'assignee_name' | 'status'
>;

const createFeedback = async (
  payload: CreateFeedbackRequest,
): Promise<ApiResponse<Feedback>> => {
  try {
    const { data } = await network.post<ApiResponse<Feedback>>(
      '/feedback',
      payload,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getFeedback = async (
  status?: FeedbackStatus,
): Promise<FeedbackListResponse> => {
  try {
    const { data } = await network.get<FeedbackListResponse>('/feedback', {
      params: status ? { status } : undefined,
    });
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateFeedback = async (
  id: number,
  payload: UpdateFeedbackRequest,
): Promise<ApiResponse<FeedbackPatchResult>> => {
  try {
    const { data } = await network.patch<ApiResponse<FeedbackPatchResult>>(
      `/feedback/${id}`,
      payload,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Die waehlbaren Arten fuer das Melde-Modal — fuer jeden Eingeloggten lesbar. */
const getTicketArten = async (): Promise<ApiResponse<TicketArt[]>> => {
  try {
    const { data } = await network.get<ApiResponse<TicketArt[]>>('/ticket-arten');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Die ganze Liste in Wunschreihenfolge speichern (nur SA). Wie bei den
 * Buchungsfeldern ergibt sich die Position aus dem Index — die Oberflaeche
 * ordnet, nicht ein Zahlenfeld.
 */
const updateTicketArten = async (
  arten: Pick<TicketArt, 'slug' | 'name' | 'is_active'>[],
): Promise<ApiResponse<TicketArt[]>> => {
  try {
    const { data } = await network.put<ApiResponse<TicketArt[]>>('/ticket-arten', { arten });
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const feedbackApi = {
  createFeedback,
  getFeedback,
  updateFeedback,
  getTicketArten,
  updateTicketArten,
};

export default feedbackApi;
